import { useEffect, useMemo, useRef } from "react";
import { X, Play, Pause } from "lucide-react";
import { useAudioReaderState, play, pause, resume, stop, splitSentences } from "@/lib/audioReader";
import AudioSettings from "@/components/AudioSettings";

// Full-screen "read along" — plays the study aloud and highlights + auto-scrolls
// the current sentence in sync with playback. The reader speaks the same text with
// the same sentence split, so the highlight tracks the voice sentence-by-sentence.
export default function ReadAlongPanel({ id, text, open, onClose }) {
  const { activeId, status, progress } = useAudioReaderState();
  const resolved = typeof text === "function" ? text() : text || "";
  const sentences = useMemo(() => splitSentences(resolved), [resolved]);
  const ranges = useMemo(() => {
    const total = sentences.reduce((a, s) => a + s.length + 1, 0) || 1;
    let acc = 0;
    return sentences.map((s) => {
      const start = acc / total;
      acc += s.length + 1;
      return [start, acc / total];
    });
  }, [sentences]);

  const isThis = activeId === id;
  let activeIdx = -1;
  if (isThis && status !== "idle") {
    const p = Math.min(0.999, progress || 0);
    activeIdx = ranges.findIndex(([a, b]) => p >= a && p < b);
    if (activeIdx < 0 && status !== "loading") activeIdx = sentences.length - 1;
    if (activeIdx < 0) activeIdx = 0;
  }

  const refs = useRef([]);
  useEffect(() => {
    if (activeIdx >= 0 && refs.current[activeIdx]) {
      refs.current[activeIdx].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeIdx]);

  useEffect(() => {
    if (open && resolved) play(id, resolved);
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  const playing = isThis && status === "playing";
  const paused = isThis && status === "paused";
  const loading = isThis && status === "loading";

  const toggle = () => {
    if (loading) return;
    if (playing) pause();
    else if (paused) resume();
    else play(id, resolved);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#07090E] flex flex-col" data-testid="readalong-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#1E273D] bg-[#0E131F]/80 backdrop-blur">
        <span className="text-xs font-medium uppercase tracking-widest text-[#00E5FF]">Read along</span>
        <div className="flex items-center gap-1">
          <AudioSettings />
          <button
            data-testid="readalong-close"
            onClick={() => { stop(); onClose(); }}
            className="rounded-full w-9 h-9 flex items-center justify-center text-slate-500 hover:text-slate-300 hover:bg-[#141B29] transition-colors"
            aria-label="Close read along"
          >
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-10">
        <p className="text-2xl md:text-3xl leading-relaxed text-slate-500 max-w-2xl mx-auto">
          {sentences.map((s, i) => (
            <span
              key={i}
              ref={(el) => (refs.current[i] = el)}
              data-testid={i === activeIdx ? "readalong-active" : undefined}
              className={`transition-colors duration-300 ${
                i === activeIdx ? "text-white bg-[#10222A] rounded px-0.5" : ""
              }`}
            >
              {s}{" "}
            </span>
          ))}
        </p>
      </div>

      <div className="px-5 py-4 border-t border-[#1E273D] bg-[#0E131F]/80 backdrop-blur flex items-center justify-center gap-4">
        <button
          data-testid="readalong-playpause"
          onClick={toggle}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-6 py-3 text-sm font-medium transition-colors active:scale-95 disabled:opacity-70 disabled:cursor-default"
        >
          {loading ? (
            <span>Starting…</span>
          ) : playing ? (
            <><Pause className="w-5 h-5" strokeWidth={1.75} /> Pause</>
          ) : (
            <><Play className="w-5 h-5" strokeWidth={1.75} /> {paused ? "Resume" : "Play"}</>
          )}
        </button>
        <div className="h-1.5 w-40 rounded-full bg-[#1E273D] overflow-hidden" data-testid="readalong-progress">
          <div className="h-full bg-[#00E5FF] transition-[width] duration-300 ease-linear"
               style={{ width: `${Math.round((isThis ? progress || 0 : 0) * 100)}%` }} />
        </div>
      </div>
    </div>
  );
}
